import { Box, Stack, Typography } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import { clamp, filter, find, first, map, sortBy } from 'lodash';
import React from 'react';
import { PeakDetectionSpacingDistanceGroup, formatPeakDetectionSpacingDistanceLabel } from './helpers';
import {
  CellSpotOutcome,
  buildCellSpotIllustration,
  cellIllustrationDrawingAxis,
  cellIllustrationMajorAxisUm,
  cellIllustrationNucleusDiameterUm,
} from './spotDetectionIllustrations';
import {
  MultiplexPeakDetectionChannelProfile,
  MultiplexPeakDetectionProfile,
  MultiplexRawDogPeakDetectionChannelProfile,
} from './types';

export const CellSpotDetectionIllustration = ({
  profile,
  useRawDog,
  panelName,
  distanceGroups = [],
  unknownResolutionCount = 0,
}: {
  profile: MultiplexPeakDetectionProfile;
  useRawDog: boolean;
  panelName: string;
  distanceGroups?: PeakDetectionSpacingDistanceGroup[];
  unknownResolutionCount?: number;
}) => {
  const theme = useTheme();
  const advanced = profile as MultiplexRawDogPeakDetectionChannelProfile;
  const signalValid = useRawDog
    ? Number.isFinite(advanced.clipMinFraction) &&
      Number.isFinite(advanced.clipMaxFraction) &&
      advanced.clipMinFraction >= 0 &&
      advanced.clipMinFraction < advanced.clipMaxFraction &&
      advanced.clipMaxFraction <= 1
    : Number.isFinite((profile as MultiplexPeakDetectionChannelProfile).threshold) &&
      (profile as MultiplexPeakDetectionChannelProfile).threshold >= 0 &&
      (profile as MultiplexPeakDetectionChannelProfile).threshold <= 1;
  if (!signalValid || !Number.isInteger(profile.minDistancePx) || profile.minDistancePx < 1)
    return (
      <Typography variant="caption" color="text.secondary">
        Enter valid cutoff and spot spacing values to show the example.
      </Typography>
    );
  const displayedResolution = first(sortBy(distanceGroups, 'umpp'));
  const illustration = buildCellSpotIllustration(profile, useRawDog, displayedResolution?.umpp);
  const cutoffLabel = useRawDog ? 'Minimum contrast' : 'Brightness cutoff';
  const valueLabel = (value: number) => String(Number(value.toFixed(4)));
  const examplePeak = find(illustration.spots, { label: 'F' })!;
  const colors: Record<CellSpotOutcome, string> = {
    eligible: theme.palette.success.main,
    signal: theme.palette.error.main,
    noise: theme.palette.info.main,
    distance: theme.palette.warning.dark,
  };
  const descriptions: Record<CellSpotOutcome, string> = {
    eligible: 'Passes shown checks',
    signal: 'Too faint',
    noise: 'Too noisy',
    distance: 'Too close',
  };
  const overviewDescriptions: Record<CellSpotOutcome, string> = {
    eligible: 'pass',
    signal: 'faint',
    noise: 'noisy',
    distance: 'close',
  };
  const markerColor = theme.palette.success.main;
  const signalLimit = Math.min(1, Math.max(0.12, illustration.cutoff * 4));
  const signalY = (value: number) => 72 - clamp(value / signalLimit, 0, 1) * 46;
  const noiseLimit = Math.max(10, (illustration.noiseCutoff || 0) + 2);
  const noiseY = (value: number) => 145 - clamp(value / noiseLimit, 0, 1) * 33;
  const distanceAxisMaximum = Math.max(
    12,
    profile.minDistancePx,
    ...map(
      filter(illustration.spots, (spot) => Number.isFinite(spot.distanceToStronger)),
      'distanceToStronger'
    )
  );
  const distanceX = (value: number) => 178 + clamp(value / distanceAxisMaximum, 0, 1) * 172;
  const strongest = filter(
    illustration.spots,
    (spot) => spot.distanceOutcome === 'eligible' && !Number.isFinite(spot.distanceToStronger)
  )[0];
  const legend: CellSpotOutcome[] = ['signal', ...(useRawDog ? ['noise' as const] : []), 'distance', 'eligible'];
  return (
    <Stack spacing={0.5}>
      <Box
        component="svg"
        role="img"
        aria-label={`Combined spot detection example for ${panelName}`}
        data-working-umpp={illustration.workingUmpp ?? undefined}
        data-cell-size-um={cellIllustrationMajorAxisUm}
        viewBox="0 0 390 276"
        sx={{
          width: 390,
          maxWidth: '100%',
          height: 276,
          display: 'block',
          '& text': { fontFamily: theme.typography.fontFamily, fontSize: '10px', fill: 'text.secondary' },
        }}
      >
        <text x="8" y="12">
          Cell membrane
        </text>
        <g transform="translate(12,55) scale(3.4)">
          <path
            d="M16 1 C28 -1 34 7 32 19 C30 30 9 33 3 23 C-2 15 1 4 9 2 Z"
            fill={alpha(theme.palette.info.main, 0.025)}
            stroke={theme.palette.text.secondary}
            strokeWidth="0.4"
          />
          <ellipse
            data-testid="example-nucleus"
            data-diameter-um={cellIllustrationNucleusDiameterUm}
            cx="16"
            cy="16"
            rx={(cellIllustrationNucleusDiameterUm * cellIllustrationDrawingAxis) / cellIllustrationMajorAxisUm / 2}
            ry={(cellIllustrationNucleusDiameterUm * cellIllustrationDrawingAxis) / cellIllustrationMajorAxisUm / 2}
            fill={alpha(theme.palette.info.main, 0.12)}
            stroke={theme.palette.info.main}
            strokeWidth="0.3"
          />
          <text x="16" y="19" textAnchor="middle" style={{ fontSize: '2.7px' }}>
            Nucleus
          </text>
          {strongest && illustration.spacingDrawingUnits != null ? (
            <rect
              data-testid="example-spacing-region"
              data-spacing-um={profile.minDistancePx * illustration.workingUmpp!}
              x={strongest.x - illustration.spacingDrawingUnits}
              y={strongest.y - illustration.spacingDrawingUnits}
              width={illustration.spacingDrawingUnits * 2}
              height={illustration.spacingDrawingUnits * 2}
              fill={alpha(theme.palette.warning.main, 0.12)}
              stroke={theme.palette.warning.dark}
              strokeWidth="0.3"
              strokeDasharray="1 1"
            />
          ) : null}
          {map(illustration.spots, (spot) => {
            const outcome = spot.distanceOutcome;
            return (
              <g
                key={spot.label}
                transform={`translate(${spot.x},${spot.y})`}
                data-testid={`example-spot-${spot.label}`}
                data-outcome={outcome}
                data-signal-outcome={spot.outcome}
              >
                <title>
                  {useRawDog
                    ? `${spot.label}: input peak ${valueLabel(spot.intensity)} → prepared peak ${valueLabel(
                        spot.preparedPeak
                      )}; prepared background ${valueLabel(spot.preparedBackground)}; effective contrast ${valueLabel(
                        spot.signal
                      )}; ${descriptions[outcome]}`
                    : `${spot.label}: normalized peak ${valueLabel(spot.signal)}; ${descriptions[outcome]}`}
                </title>
                <circle
                  data-testid={`example-dot-${spot.label}`}
                  r="1.3"
                  fill={markerColor}
                  fillOpacity={spot.expressionOpacity}
                  stroke={colors[outcome]}
                  strokeWidth="0.35"
                />
                {outcome !== 'eligible' ? (
                  <path d="M-0.9 -0.9 L0.9 0.9 M-0.9 0.9 L0.9 -0.9" stroke={colors[outcome]} strokeWidth="0.35" />
                ) : null}
                <text
                  x={spot.label === 'E' ? 3 : 0}
                  y="-2.3"
                  textAnchor="middle"
                  style={{ fontSize: '2.8px', fontWeight: 700 }}
                >
                  {spot.label}
                </text>
              </g>
            );
          })}
          <line
            data-testid="example-cell-major-axis"
            data-size-um={cellIllustrationMajorAxisUm}
            data-working-pixels={illustration.cellMajorAxisPixels ?? undefined}
            x1="0"
            x2={cellIllustrationDrawingAxis}
            y1="34"
            y2="34"
            stroke={theme.palette.text.secondary}
            strokeWidth="0.25"
          />
          <line x1="0" x2="0" y1="33" y2="35" stroke={theme.palette.text.secondary} strokeWidth="0.25" />
          <line
            x1={cellIllustrationDrawingAxis}
            x2={cellIllustrationDrawingAxis}
            y1="33"
            y2="35"
            stroke={theme.palette.text.secondary}
            strokeWidth="0.25"
          />
          <text x={cellIllustrationDrawingAxis / 2} y="38" textAnchor="middle" style={{ fontSize: '2.7px' }}>
            {cellIllustrationMajorAxisUm} µm
            {illustration.cellMajorAxisPixels != null
              ? ` / ${valueLabel(illustration.cellMajorAxisPixels)} px`
              : ' (example)'}
          </text>
        </g>
        <g>
          <text x="173" y="12">
            {useRawDog ? 'Effective contrast after clipping' : 'Normalized peak brightness'}
          </text>
          <line x1="174" x2="368" y1="72" y2="72" stroke={theme.palette.divider} />
          {map(illustration.spots, (spot, index) => (
            <g key={spot.label}>
              <rect
                data-testid={`signal-level-${spot.label}`}
                x={181 + index * 31}
                y={signalY(spot.signal)}
                width="15"
                height={72 - signalY(spot.signal)}
                fill={markerColor}
                fillOpacity={spot.expressionOpacity}
                stroke={colors[spot.outcome]}
                strokeWidth="0.6"
              />
              <text x={188.5 + index * 31} y="85" textAnchor="middle">
                {spot.label}
              </text>
              <text
                data-testid={`effective-peak-value-${spot.label}`}
                x={188.5 + index * 31}
                y={Math.max(23, signalY(spot.signal) - 3)}
                textAnchor="middle"
                style={{ fontSize: '8px' }}
              >
                {spot.signal > signalLimit ? '+' : ''}
                {valueLabel(spot.signal)}
              </text>
            </g>
          ))}
          <line
            data-testid="spot-signal-cutoff"
            data-cutoff={illustration.cutoff}
            data-label={cutoffLabel}
            x1="174"
            x2="368"
            y1={signalY(illustration.cutoff)}
            y2={signalY(illustration.cutoff)}
            stroke={theme.palette.error.main}
            strokeWidth="1.4"
            strokeDasharray="4 3"
          />
          <text x="369" y={signalY(illustration.cutoff) - 4} textAnchor="end">
            {useRawDog ? 'Min contrast' : 'Cutoff'} {illustration.cutoff}
          </text>
          <text x="174" y="98">{`Scale 0-${signalLimit.toFixed(2)}; + exceeds scale`}</text>
          {useRawDog ? (
            <>
              <text x="173" y="110">
                Noise filter (background units)
              </text>
              {map(illustration.spots, (spot, index) => (
                <rect
                  key={spot.label}
                  data-testid={`noise-level-${spot.label}`}
                  x={181 + index * 31}
                  y={noiseY(spot.noiseUnits)}
                  width="15"
                  height={145 - noiseY(spot.noiseUnits)}
                  fill={markerColor}
                  fillOpacity={spot.expressionOpacity}
                  stroke={colors[spot.outcome]}
                  strokeWidth="0.6"
                />
              ))}
              <line
                data-testid="spot-noise-cutoff"
                data-cutoff={illustration.noiseCutoff}
                x1="174"
                x2="368"
                y1={noiseY(illustration.noiseCutoff!)}
                y2={noiseY(illustration.noiseCutoff!)}
                stroke={theme.palette.info.main}
                strokeWidth="1.4"
                strokeDasharray="4 3"
              />
              <text x="369" y={noiseY(illustration.noiseCutoff!) - 4} textAnchor="end">
                Noise cutoff {illustration.noiseCutoff}
              </text>
              {map(illustration.spots, (spot, index) => (
                <text key={spot.label} x={188.5 + index * 31} y="159" textAnchor="middle">
                  {spot.label}
                </text>
              ))}
            </>
          ) : (
            <text x="173" y="124">
              Only candidates above the cutoff proceed.
            </text>
          )}
        </g>
        <g data-testid="example-distance-chart">
          <text x="174" y="178">
            Distance to stronger spot (px)
          </text>
          {illustration.workingUmpp != null ? (
            <>
              <rect
                x="178"
                y="194"
                width={distanceX(profile.minDistancePx) - 178}
                height="62"
                fill={alpha(theme.palette.warning.main, 0.08)}
              />
              <line
                data-testid="spot-distance-cutoff"
                data-cutoff={profile.minDistancePx}
                x1={distanceX(profile.minDistancePx)}
                x2={distanceX(profile.minDistancePx)}
                y1="193"
                y2="257"
                stroke={colors.distance}
                strokeDasharray="4 3"
              />
              <text x={distanceX(profile.minDistancePx)} y="190" textAnchor="middle">
                {profile.minDistancePx} px minimum
              </text>
              {map(
                filter(
                  illustration.spots,
                  (spot) => spot.outcome === 'eligible' && Number.isFinite(spot.distanceToStronger)
                ),
                (spot, index, candidates) => {
                  const yPosition = 209 + index * (candidates.length > 1 ? 42 / (candidates.length - 1) : 0);
                  const distance = Number.isFinite(spot.distanceToStronger) ? spot.distanceToStronger : 0;
                  return (
                    <g key={spot.label}>
                      <line
                        x1="178"
                        x2={distanceX(distance)}
                        y1={yPosition}
                        y2={yPosition}
                        stroke={theme.palette.divider}
                      />
                      <circle
                        data-testid={`distance-level-${spot.label}`}
                        cx={distanceX(distance)}
                        cy={yPosition}
                        r="4"
                        fill={markerColor}
                        fillOpacity={spot.expressionOpacity}
                        stroke={colors[spot.distanceOutcome]}
                      />
                      <text x="168" y={yPosition + 3} textAnchor="end">
                        {spot.label}
                      </text>
                      <text
                        x={Math.min(345, distanceX(distance) + 8)}
                        y={yPosition - 7}
                        textAnchor={distanceX(distance) > 323 ? 'end' : 'start'}
                      >
                        {Number.isFinite(spot.distanceToStronger)
                          ? `${valueLabel(spot.distanceToStronger)} px`
                          : 'Stronger peak'}
                      </text>
                    </g>
                  );
                }
              )}
              <text x="174" y="272">
                Shaded region: weaker spot removed
              </text>
            </>
          ) : (
            <text x="174" y="212">
              Resolution missing: distance not illustrated
            </text>
          )}
        </g>
        <g>
          {useRawDog ? (
            <>
              <text data-testid="spot-clipping-factor" data-factor={illustration.clippingFactor} x="8" y="208">
                Clipping factor: {illustration.clippingFactor}
              </text>
              <text x="8" y="223">
                Upper: {valueLabel(illustration.upperBound)} × slide max
              </text>
              <text
                data-testid="spot-lower-clipping-level"
                data-cutoff={Number(illustration.lowerBound.toFixed(6))}
                x="8"
                y="238"
              >
                Lower: {valueLabel(illustration.lowerBound)} × slide max
              </text>
              <text data-testid="spot-slide-signal-maximum" x="8" y="253">
                Slide max: 1 (example)
              </text>
            </>
          ) : (
            <text x="8" y="208">
              Normalized example range: 0–1
            </text>
          )}
        </g>
      </Box>
      <Stack data-testid="spot-reason-summary" direction="row" spacing={1} useFlexGap flexWrap="wrap">
        {map(legend, (outcome) => (
          <Typography key={outcome} variant="caption" sx={{ color: colors[outcome] }}>
            {filter(illustration.spots, (spot) => spot.distanceOutcome === outcome).length}{' '}
            {overviewDescriptions[outcome]}
          </Typography>
        ))}
      </Stack>
      <Typography variant="caption" color="text.secondary">
        Illustration only. Colors show why spots pass or are excluded.
      </Typography>
      {displayedResolution && (
        <Typography variant="caption" color="text.secondary">
          Spacing: {profile.minDistancePx} px = {formatPeakDetectionSpacingDistanceLabel(displayedResolution)}.
          {distanceGroups.length > 1
            ? ` Example scale: ${displayedResolution.umpp} µm/px; ${distanceGroups.length} resolution groups.`
            : ''}
        </Typography>
      )}
      {unknownResolutionCount > 0 && (
        <Typography variant="caption" color="text.secondary">
          Resolution unavailable for {unknownResolutionCount} {unknownResolutionCount === 1 ? 'slide' : 'slides'}.
        </Typography>
      )}
      <Box component="details">
        <Typography component="summary" variant="caption" sx={{ cursor: 'pointer' }}>
          Technical details
        </Typography>
        <Stack spacing={1} sx={{ mt: 1 }}>
          {useRawDog && (
            <Typography variant="caption" color="text.secondary">
              Background floor: {illustration.clippingFactor} × {valueLabel(illustration.upperBound)} ={' '}
              {valueLabel(illustration.lowerBound)} of the highest slide signal (per marker, whole-slide overview). The
              contrast cutoff is separate.
            </Typography>
          )}
          <Typography variant="caption" color="text.secondary" data-testid="spot-effective-peak-explanation">
            Example F {useRawDog ? '(slide max = 1)' : '(normalized signal)'}:{' '}
            <span
              data-testid="example-peak-F"
              data-input-value={examplePeak.intensity}
              data-prepared-value={examplePeak.preparedPeak}
            >
              {useRawDog
                ? `input peak ${valueLabel(examplePeak.intensity)} → prepared ${valueLabel(examplePeak.preparedPeak)}`
                : `normalized peak ${valueLabel(examplePeak.preparedPeak)}`}
            </span>
            {useRawDog ? (
              <>
                {' '}
                −{' '}
                <span
                  data-testid="example-background-F"
                  data-input-value={examplePeak.background}
                  data-prepared-value={examplePeak.preparedBackground}
                >
                  prepared background {valueLabel(examplePeak.preparedBackground)}
                </span>
              </>
            ) : null}{' '}
            ={' '}
            <span data-testid="example-effective-signal-F" data-value={examplePeak.signal}>
              {useRawDog ? 'effective contrast' : 'effective peak'} {valueLabel(examplePeak.signal)}
            </span>
            . {cutoffLabel}: {valueLabel(illustration.cutoff)}
            {useRawDog ? ' (separate requirement)' : ' (your setting)'}.
          </Typography>
          <Stack data-testid="spot-resolution-context" spacing={0.25}>
            <Typography variant="caption" color="text.secondary">
              Illustrative tumor cell: {cellIllustrationMajorAxisUm} µm axis; nucleus:{' '}
              {cellIllustrationNucleusDiameterUm} µm. Not a universal size or slide measurement.
            </Typography>
            {displayedResolution ? (
              <Typography variant="caption" color="text.secondary">
                Showing {displayedResolution.umpp} µm/px {displayedResolution.isCustom ? '(custom)' : '(native)'}.
              </Typography>
            ) : null}
            {map(distanceGroups, (group) => (
              <Typography key={`${group.isCustom}-${group.umpp}`} variant="caption" color="text.secondary">
                {profile.minDistancePx} px = {formatPeakDetectionSpacingDistanceLabel(group)};{' '}
                {cellIllustrationMajorAxisUm} µm cell = {valueLabel(cellIllustrationMajorAxisUm / group.umpp)} working
                px ({group.slideCount} {group.slideCount === 1 ? 'slide' : 'slides'}).
              </Typography>
            ))}
            {unknownResolutionCount ? (
              <Typography variant="caption" color="text.secondary">
                Resolution unavailable for {unknownResolutionCount} {unknownResolutionCount === 1 ? 'slide' : 'slides'};
                no physical pixel-distance conversion.
              </Typography>
            ) : null}
          </Stack>
        </Stack>
      </Box>
    </Stack>
  );
};
